import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogContent,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  Paper,
  alpha,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowBack as BackIcon,
  ArrowForward as NextIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { CreateSiteData, Template } from "../../types";

interface CreateSiteModalProps {
  divisionId: number;
  templates: Template[];
  onClose: () => void;
  onCreate: (data: CreateSiteData) => Promise<void>;
}

// Template presets with theme colors
const TEMPLATE_PRESETS = [
  {
    id: "beauty-clinic",
    name: "Beauty Clinic",
    emoji: "💅",
    description: "สำหรับคลินิกความงาม สปา",
    primaryColor: "#ec4899",
    secondaryColor: "#f472b6",
  },
  {
    id: "medical-center",
    name: "Medical Center",
    emoji: "🏥",
    description: "สำหรับโรงพยาบาล คลินิกทั่วไป",
    primaryColor: "#3b82f6",
    secondaryColor: "#60a5fa",
  },
  {
    id: "modern-clinic",
    name: "Modern Clinic",
    emoji: "🦷",
    description: "สำหรับคลินิกทันตกรรม เฉพาะทาง",
    primaryColor: "#10b981",
    secondaryColor: "#34d399",
  },
  {
    id: "blank",
    name: "Blank",
    emoji: "📄",
    description: "เริ่มจากหน้าว่าง",
    primaryColor: "#6b7280",
    secondaryColor: "#9ca3af",
  },
];

const STEPS = ["ข้อมูลเบื้องต้น", "เลือก Template"];

function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export const CreateSiteModal: React.FC<CreateSiteModalProps> = ({ divisionId, templates, onClose, onCreate }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [isCreating, setIsCreating] = useState(false);

  // Step 1 data
  const [siteName, setSiteName] = useState("");
  const [siteSlug, setSiteSlug] = useState("");
  const [phone, setPhone] = useState("");

  // Step 2 data
  const [selectedTemplate, setSelectedTemplate] = useState("beauty-clinic");
  const [primaryColor, setPrimaryColor] = useState("#ec4899");
  const [secondaryColor, setSecondaryColor] = useState("#f472b6");

  const handleNameChange = (value: string) => {
    setSiteName(value);
    setSiteSlug(slugify(value));
  };

  const handlePhoneChange = (value: string) => {
    // Only allow numbers
    const cleaned = value.replace(/[^0-9]/g, "");
    setPhone(cleaned);
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const preset = TEMPLATE_PRESETS.find((t) => t.id === templateId);
    if (preset) {
      setPrimaryColor(preset.primaryColor);
      setSecondaryColor(preset.secondaryColor);
    }
  };

  const handleNext = () => {
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleCreate = async () => {
    if (!siteName.trim()) return;

    setIsCreating(true);
    try {
      await onCreate({
        divisionId,
        name: siteName.trim(),
        slug: siteSlug || slugify(siteName),
        phone: phone.trim() || undefined,
        templateId: selectedTemplate,
        theme: {
          primaryColor,
          secondaryColor,
        },
      });
    } catch (error) {
      console.error("Error creating site:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const canProceedStep1 = siteName.trim().length > 0;
  const canCreate = canProceedStep1 && selectedTemplate;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "white",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" color="text.primary" fontWeight={600}>
            สร้าง Site ใหม่
          </Typography>
          <IconButton onClick={onClose} sx={{ color: "grey.600" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Stepper */}
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel
                sx={{
                  "& .MuiStepLabel-label": { color: "text.secondary" },
                  "& .MuiStepLabel-label.Mui-active": { color: "text.primary" },
                  "& .MuiStepLabel-label.Mui-completed": { color: "success.main" },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        <DialogContent sx={{ p: 0 }}>
          {/* Step 1: Basic Info */}
          {activeStep === 0 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  ชื่อ Site *
                </Typography>
                <TextField
                  fullWidth
                  value={siteName}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="My Beauty Clinic"
                  autoFocus
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "grey.50",
                      "& fieldset": { borderColor: "grey.300" },
                      "&:hover fieldset": { borderColor: "grey.400" },
                      "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    },
                    "& .MuiInputBase-input": { color: "text.primary" },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Slug (URL)
                </Typography>
                <TextField
                  fullWidth
                  value={siteSlug}
                  onChange={(e) => setSiteSlug(slugify(e.target.value))}
                  placeholder="my-beauty-clinic"
                  InputProps={{
                    startAdornment: (
                      <Typography color="text.secondary" sx={{ mr: 0.5 }}>
                        /
                      </Typography>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "grey.50",
                      "& fieldset": { borderColor: "grey.300" },
                      "&:hover fieldset": { borderColor: "grey.400" },
                      "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    },
                    "& .MuiInputBase-input": { color: "text.primary" },
                  }}
                />
              </Box>

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  เบอร์โทร (optional)
                </Typography>
                <TextField
                  fullWidth
                  value={phone}
                  onChange={(e) => handlePhoneChange(e.target.value)}
                  placeholder="0812345678"
                  inputProps={{
                    inputMode: "tel",
                  }}
                  helperText="กรอกได้เฉพาะตัวเลขเท่านั้น"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "grey.50",
                      "& fieldset": { borderColor: "grey.300" },
                      "&:hover fieldset": { borderColor: "grey.400" },
                      "&.Mui-focused fieldset": { borderColor: "primary.main" },
                    },
                    "& .MuiInputBase-input": { color: "text.primary" },
                    "& .MuiFormHelperText-root": { color: "text.secondary" },
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Step 2: Template & Theme */}
          {activeStep === 1 && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Template Selection */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  เลือก Template
                </Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
                  {TEMPLATE_PRESETS.map((template) => (
                    <Paper
                      key={template.id}
                      onClick={() => handleTemplateSelect(template.id)}
                      sx={{
                        p: 2,
                        cursor: "pointer",
                        bgcolor: selectedTemplate === template.id ? alpha(template.primaryColor, 0.15) : "grey.50",
                        border: 2,
                        borderColor: selectedTemplate === template.id ? template.primaryColor : "grey.200",
                        borderRadius: 2,
                        transition: "all 0.2s",
                        "&:hover": {
                          bgcolor: alpha(template.primaryColor, 0.1),
                          borderColor: alpha(template.primaryColor, 0.5),
                        },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: template.primaryColor,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "1.25rem",
                          }}
                        >
                          {template.emoji}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography color="text.primary" fontWeight={500} fontSize="0.9rem">
                            {template.name}
                          </Typography>
                        </Box>
                        {selectedTemplate === template.id && (
                          <CheckIcon sx={{ color: template.primaryColor, fontSize: 20 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {template.description}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </Box>

              {/* Color Customization */}
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  สี Theme
                </Typography>
                <Box sx={{ display: "flex", gap: 3 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Primary
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        component="input"
                        type="color"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        sx={{
                          width: 48,
                          height: 48,
                          border: "none",
                          borderRadius: 2,
                          cursor: "pointer",
                          p: 0,
                          bgcolor: "transparent",
                        }}
                      />
                      <TextField
                        size="small"
                        value={primaryColor}
                        onChange={(e) => setPrimaryColor(e.target.value)}
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.300" },
                          },
                          "& .MuiInputBase-input": { color: "text.primary", fontFamily: "monospace" },
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                      Secondary
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        component="input"
                        type="color"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        sx={{
                          width: 48,
                          height: 48,
                          border: "none",
                          borderRadius: 2,
                          cursor: "pointer",
                          p: 0,
                          bgcolor: "transparent",
                        }}
                      />
                      <TextField
                        size="small"
                        value={secondaryColor}
                        onChange={(e) => setSecondaryColor(e.target.value)}
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            bgcolor: "grey.50",
                            "& fieldset": { borderColor: "grey.300" },
                          },
                          "& .MuiInputBase-input": { color: "text.primary", fontFamily: "monospace" },
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Preview */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                }}
              >
                <Typography color="white" fontWeight={600} fontSize="1.1rem">
                  {siteName || "Your Site Name"}
                </Typography>
                <Typography color="rgba(255,255,255,0.8)" variant="body2">
                  Preview ของสี theme ที่เลือก
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box
          sx={{ display: "flex", justifyContent: "space-between", mt: 4, pt: 3, borderTop: 1, borderColor: "grey.200" }}
        >
          <Button
            onClick={activeStep === 0 ? onClose : handleBack}
            startIcon={activeStep > 0 ? <BackIcon /> : undefined}
            sx={{ color: "grey.600" }}
          >
            {activeStep === 0 ? "ยกเลิก" : "ย้อนกลับ"}
          </Button>

          {activeStep === STEPS.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={!canCreate || isCreating}
              startIcon={isCreating ? undefined : <CheckIcon />}
              sx={{
                px: 4,
                background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
                "&:hover": {
                  background: `linear-gradient(135deg, ${secondaryColor} 0%, ${primaryColor} 100%)`,
                },
              }}
            >
              {isCreating ? "กำลังสร้าง..." : "สร้าง Site"}
            </Button>
          ) : (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceedStep1}
              endIcon={<NextIcon />}
              sx={{ px: 4 }}
            >
              ถัดไป
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};
